import * as React from 'react'
import { Limit } from '../../components'
import { Block } from 'baseui/block';
import { Input } from "baseui/input";
import { ICounter } from './types'
import { FormControl } from "baseui/form-control";
import {
    Checkbox,
    STYLE_TYPE
} from "baseui/checkbox";
// api
import { useSetObjectValue } from '../../../hooks/useSetObjectValue';
import { MdTimer } from 'react-icons/md';
import { FormattedMessage, useIntl } from 'react-intl';
import { ValueDisplayWithLabel } from '../../components/ValueDisplay';
import { Parameters } from '../BaseCounter/Parameters';
import { Tabs, Tab } from "baseui/tabs-motion";
import { ReferencedRates } from '../BaseCounter';
import { useStyletron } from 'baseui';
import { useGetCounterRates } from '../../../hooks/useGetCounterRates';
import { HintDot, NotificationCircle } from 'baseui/badge';
import { LiaFileInvoiceDollarSolid } from 'react-icons/lia'

export const ConfigurationPanel = (props: ICounter) => {
    const intl = useIntl();
    const { count } = useGetCounterRates(props.id, props.resourceGroupId);

    const [css, theme] = useStyletron()
    const [activeKey, setActiveKey] = React.useState<React.Key>(0);
    const [alarmDelay, setAlarmDelay, setAlarmDelayResult] = useSetObjectValue(props.id, props.resourceGroupId, "errDelay", props.errDelay);
    const onAlarmDelayChanged = (e: any) => setAlarmDelay(parseFloat(e.target.value));

    const rangeMin = 0;
    const rangeMax = 1000;

    // alarm limit low
    const [alarmLimitLow, setAlarmLimitLow] = useSetObjectValue(props.id, props.resourceGroupId, "limitLow", props.limitLow);
    const [enableAlarmLimitLow, setEnableAlarmLimitLow] = useSetObjectValue(props.id, props.resourceGroupId, "enableLimitLow", props.enableLimitLow, 10);

    const onAlarmLimitLowChanged = (newvalue: number) => {
        const alarmLimitLowLimitedValue = Math.max(newvalue, rangeMin);
        //const alarmLimitLowLimitedValue = newvalue;
        setAlarmLimitLow(alarmLimitLowLimitedValue);
    }

    // alarm limit high
    const [alarmLimitHigh, setAlarmLimitHigh] = useSetObjectValue(props.id, props.resourceGroupId, "limitHigh", props.limitHigh);
    const [enableAlarmLimitHigh, setEnableAlarmLimitHigh] = useSetObjectValue(props.id, props.resourceGroupId, "enableLimitHigh", props.enableLimitHigh, 10);
    const onAlarmLimitHighChanged = (newvalue: number) => {
        const alarmLimitHighLimitedValue = Math.min(newvalue, rangeMax);
        //const alarmLimitHighLimitedValue = newvalue;
        setAlarmLimitHigh(alarmLimitHighLimitedValue);
    }

    const [enableCounter, setEnableCounter] = useSetObjectValue(props.id, props.resourceGroupId, "enableCounter", props.enableCounter, 10);
    const onEnableCounterChanged = (e: any) => { if (e) setEnableCounter(true); else setEnableCounter(false); }


    const dimPower = props.dimension === 'kWh' ? 'kW' : props.dimension === 'MWh' ? 'kW' : props.dimension === 'm³' ? 'l' : props.dimension === 'l' ? 'l' : '[]'


    const lLow = props.limitLow;
    const low = lLow == null ? (<></>) :
        (
            <Block gridRowGap='0px' display='flex' flexDirection='column' >
                <Limit
                    enable={enableAlarmLimitLow}
                    min={rangeMin}
                    max={rangeMax}
                    dimension={dimPower}
                    value={alarmLimitLow}
                    onEnableChanged={setEnableAlarmLimitLow}
                    onValueChanged={onAlarmLimitLowChanged}
                    name={<FormattedMessage id="Devices.Low" defaultMessage="Low" />}></Limit>
            </Block>
        );

    const lHigh = props.limitHigh;
    const high = lHigh == null ? (<></>) :
        (
            <Block gridRowGap='0px' display='flex' flexDirection='column' >
                <Limit
                    enable={enableAlarmLimitHigh}
                    min={rangeMin}
                    max={rangeMax}
                    dimension={dimPower}
                    value={alarmLimitHigh}
                    onEnableChanged={setEnableAlarmLimitHigh}
                    onValueChanged={onAlarmLimitHighChanged}
                    name={<FormattedMessage id="Devices.High" defaultMessage="High" />}></Limit>
            </Block>
        );


    const ratesTitle = count < 2 ?
        <div className={css({ display: 'flex', alignItems: 'baseline', gap: '5px' })}>
            <FormattedMessage id="Counter.Rates" defaultMessage="Rates" />
            <NotificationCircle content={count} color={'negative'}  ></NotificationCircle >
        </div>
        :
        <div className={css({ display: 'flex', alignItems: 'baseline', gap: '5px' })}>
            <FormattedMessage id="Counter.Rates" defaultMessage="Rates" />
            <NotificationCircle content={count} ></NotificationCircle>
        </div>

    return (
        <>
            <Block marginBottom={'25px'} >
                <ValueDisplayWithLabel label={intl.formatMessage({id: 'counter.Total', defaultMessage: 'Total'})} {...props} small ></ValueDisplayWithLabel>
            </Block>
            <FormControl
                caption={<FormattedMessage id="Devices.TextEnableCounter" defaultMessage="Activate the counter after commissioning if the values ​​are valid. Disable the counter when replacing a counter or when the counter returns invalid values." />}>
                <Checkbox
                    checked={enableCounter}
                    checkmarkType={STYLE_TYPE.toggle_round}
                    onChange={(e: any) => { onEnableCounterChanged(e.target.checked) }}
                    disabled={props.value === 0}>
                    <FormattedMessage id="Devices.EnableCounter" defaultMessage="Enable Counter" />
                </Checkbox>
            </FormControl>

            <Tabs
                activeKey={activeKey}
                onChange={({ activeKey }) => {
                    setActiveKey(activeKey);
                }}
                activateOnFocus
            >
                <Tab title={<FormattedMessage id="P_Building.Basic" defaultMessage="Basic" />}>
                    <>
                        <Parameters {...props}></Parameters>
                    </>
                </Tab>
                <Tab title={ratesTitle} artwork={() => <LiaFileInvoiceDollarSolid></LiaFileInvoiceDollarSolid>}>
                    <ReferencedRates {...props}></ReferencedRates>
                </Tab>
                <Tab title={<FormattedMessage id="Devices.AlarmLimits" defaultMessage="Alarm Limits" />}><>
                    <FormControl
                        label={<FormattedMessage id="Devices.AlarmDelay" defaultMessage="Alarm Delay" />}
                        caption={<FormattedMessage id="Devices.TextAlarmDelay" defaultMessage="delay until an alarm is generated" />}
                        error={setAlarmDelayResult.isError}
                    >
                        <Input
                            value={alarmDelay}
                            onChange={onAlarmDelayChanged}
                            startEnhancer={<MdTimer />}
                            endEnhancer='sec'
                            type='number'
                        />
                    </FormControl>
                    {low}
                    {high}
                </></Tab>
            </Tabs>



        </>
    )
}
